// DeviceDetectionService.js
// 기기 감지 및 세션스토리지 관리 서비스

export const DEVICE_TYPES = {
  DESKTOP: "desktop",
  MOBILE: "mobile",
  ANDROID: "android",
  IOS: "ios",
};

class DeviceDetectionService {
  /**
   * 현재 기기 타입을 감지합니다
   * @returns {string} DEVICE_TYPES 중 하나
   */
  static detectDevice() {
    const userAgent = navigator.userAgent.toLowerCase();
    const screenWidth = window.innerWidth;

    console.log("🔍 기기 감지 시작:", {
      userAgent: userAgent.substring(0, 100) + "...",
      screenWidth,
      innerHeight: window.innerHeight,
    });

    // 안드로이드 기기 감지
    if (/android/.test(userAgent)) {
      console.log("📱 안드로이드 기기 감지됨");
      return DEVICE_TYPES.ANDROID;
    }

    // iOS 기기 감지 (iPhone, iPad, iPod)
    if (/iphone|ipad|ipod/.test(userAgent)) {
      console.log("🍎 iOS 기기 감지됨");
      return DEVICE_TYPES.IOS;
    }

    // 화면 크기 기반 모바일 감지 (태블릿 포함)
    if (screenWidth <= 768) {
      console.log("📱 모바일 화면 크기 감지됨 (≤768px)");
      return DEVICE_TYPES.MOBILE;
    }

    // 기본값: 데스크탑
    console.log("🖥️ 데스크탑 기기로 판단됨");
    return DEVICE_TYPES.DESKTOP;
  }

  /**
   * 기기 타입을 세션스토리지에 저장합니다
   * @param {string} deviceType - DEVICE_TYPES 중 하나
   */
  static saveToSession(deviceType) {
    try {
      sessionStorage.setItem("deviceType", deviceType);
      console.log("💾 기기 타입 세션 저장:", deviceType);
    } catch (error) {
      console.error("❌ 세션스토리지 저장 실패:", error);
    }
  }

  /**
   * 세션스토리지에서 기기 타입을 가져옵니다
   * 없으면 새로 감지합니다
   * @returns {string} DEVICE_TYPES 중 하나
   */
  static getFromSession() {
    try {
      const savedDeviceType = sessionStorage.getItem("deviceType");

      if (
        savedDeviceType &&
        Object.values(DEVICE_TYPES).includes(savedDeviceType)
      ) {
        console.log("📂 세션에서 기기 타입 로드:", savedDeviceType);
        return savedDeviceType;
      }
    } catch (error) {
      console.error("❌ 세션스토리지 읽기 실패:", error);
    }

    // 세션에 없거나 오류 시 새로 감지
    const detectedType = this.detectDevice();
    this.saveToSession(detectedType);
    return detectedType;
  }

  /**
   * 기기 타입을 강제로 업데이트합니다
   * @param {string} deviceType - DEVICE_TYPES 중 하나
   */
  static updateDeviceType(deviceType) {
    if (Object.values(DEVICE_TYPES).includes(deviceType)) {
      this.saveToSession(deviceType);
      console.log("🔄 기기 타입 강제 업데이트:", deviceType);
      return deviceType;
    } else {
      console.error("❌ 유효하지 않은 기기 타입:", deviceType);
      return this.getFromSession();
    }
  }

  /**
   * 현재 기기가 모바일인지 확인합니다
   * @returns {boolean}
   */
  static isMobile() {
    const deviceType = this.getFromSession();
    return [
      DEVICE_TYPES.MOBILE,
      DEVICE_TYPES.ANDROID,
      DEVICE_TYPES.IOS,
    ].includes(deviceType);
  }

  /**
   * 현재 기기가 안드로이드인지 확인합니다
   * @returns {boolean}
   */
  static isAndroid() {
    return this.getFromSession() === DEVICE_TYPES.ANDROID;
  }

  /**
   * 현재 기기가 데스크탑인지 확인합니다
   * @returns {boolean}
   */
  static isDesktop() {
    return this.getFromSession() === DEVICE_TYPES.DESKTOP;
  }

  /**
   * 세션스토리지를 초기화하고 기기를 재감지합니다
   * @returns {string} 새로 감지된 기기 타입
   */
  static refresh() {
    try {
      sessionStorage.removeItem("deviceType");
      console.log("🔄 기기 감지 초기화 및 재감지");
    } catch (error) {
      console.error("❌ 세션스토리지 초기화 실패:", error);
    }

    return this.getFromSession();
  }
}

export default DeviceDetectionService;
